import { useCallback, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { createTournamentSeries, listTables, ApiError } from '../../lib/api';
import { Button } from '../../components/Button';
import { useToast } from '../../hooks/useToast';
import { PS, T } from '../../lib/theme';
import {
  EMPTY_TOURNAMENT_SERIES_FORM,
  buildCreateTournamentSeriesBody,
  tournamentSeriesFormErrors,
  type TournamentSeriesFormValue,
} from '../../lib/tournament-series-form';
import type { AdminTournamentSeriesCreateResponse, TableEmbed } from '../../lib/types';
import { TournamentSeriesFieldsForm } from './TournamentSeriesFieldsForm';
import { SeriesCreateOutcome } from './SeriesCreateOutcome';

export function NewTournamentSeriesPage() {
  const navigate = useNavigate();
  const { push } = useToast();

  const tablesLoader = useCallback((signal: AbortSignal) => listTables(signal), []);
  const tables = useApi<TableEmbed[]>(tablesLoader);

  const [form, setForm] = useState<TournamentSeriesFormValue>(EMPTY_TOURNAMENT_SERIES_FORM);
  const [submitting, setSubmitting] = useState(false);
  // The full create response, once submitted. Kept (not cleared on every
  // keystroke, only on a fresh submit attempt) so the bootstrap report stays
  // on screen — with the form exactly as submitted — while staff review it.
  // The create call never throws for a scheduling conflict (api#156
  // skip-and-report), so this is a success-path render, not an error banner.
  const [result, setResult] = useState<AdminTournamentSeriesCreateResponse | null>(null);

  const patch = (p: Partial<TournamentSeriesFormValue>) => setForm((f) => ({ ...f, ...p }));

  const errors = tournamentSeriesFormErrors(form);
  const canSubmit = errors.length === 0 && !submitting;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setResult(null);
    try {
      const body = buildCreateTournamentSeriesBody(form);
      const res = await createTournamentSeries(body);
      setResult(res);
      const skippedCount = res.occurrences_skipped.length;
      push(
        skippedCount > 0 ? 'error' : 'success',
        `Series #${res.series.id} created. ${res.occurrences_created.length} materialized` +
          (skippedCount > 0 ? `, ${skippedCount} skipped — see below.` : '.'),
      );
      // Deliberately does not navigate away: the AC requires the form to stay
      // exactly as submitted alongside the per-date report so staff can
      // review skipped dates before moving on. Staff follow the link below
      // to the series detail page when ready.
    } catch (err) {
      push('error', err instanceof ApiError ? err.message : 'Create failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 800 }}>
      <header>
        <h1 style={{ ...T.sectionTitle, margin: 0 }}>New tournament series</h1>
        <p style={{ ...T.caption, color: PS.textSec, marginTop: 4 }}>
          Bootstraps the next 28 days of occurrences immediately; the API keeps materializing on a
          rolling basis after that.
        </p>
      </header>

      <form
        onSubmit={(e) => void handleSubmit(e)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
          background: PS.white,
          padding: 20,
          border: `1px solid ${PS.borderLight}`,
          borderRadius: 4,
        }}
      >
        <TournamentSeriesFieldsForm
          value={form}
          onChange={patch}
          tables={tables.data ?? []}
          tablesLoading={tables.loading}
        />

        {errors.length > 0 && (
          <ul style={{ ...T.caption, color: PS.errorText, margin: 0, paddingLeft: 18 }}>
            {errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <Button type="submit" disabled={!canSubmit}>
            {submitting ? 'Creating…' : 'Create series'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/tournaments/series')}
            disabled={submitting}
          >
            Cancel
          </Button>
        </div>
      </form>

      {result && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <SeriesCreateOutcome created={result.occurrences_created} skipped={result.occurrences_skipped} />
          <Link
            to={`/tournaments/series/${result.series.id}`}
            style={{ color: PS.linkBlue, ...T.navLink, textTransform: 'uppercase' as const }}
          >
            View series #{result.series.id} →
          </Link>
        </div>
      )}
    </div>
  );
}
